import { useEffect } from 'react';
import {
  useSelectedOrganizationId,
  useAuthtoken,
  logout,
} from 'stores/useAuthStore';
import { queryOptions, useQuery } from '@tanstack/react-query';

import request from 'utils/request';
import { showSnackbar } from 'stores/useUiStore';
import type { ApiRespBody } from 'types/api';

import { TOKEN_INVALID } from 'containers/Base/useBaseViewData';
import type { Document } from '../docs.types';

export const DOCS_LIST_QUERY_KEY = ['docsList'] as const;

export async function fetchDocsList(
  authToken: string,
  signal?: AbortSignal
): Promise<Document[]> {
  const result = await request<ApiRespBody<Document[]>>('/api/docs', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', authorization: authToken },
    ...(signal ? { signal } : {}),
  });

  if (result?.success && result.data) return result.data;
  if (result?.message === TOKEN_INVALID) throw new Error(TOKEN_INVALID);
  throw new Error(result?.message || 'Failed to load documents');
}

export function docsListQueryOptions(
  authToken: string,
  organizationId: number | string | null
) {
  return queryOptions({
    queryKey: [...DOCS_LIST_QUERY_KEY, organizationId],
    queryFn: ({ signal }) => fetchDocsList(authToken, signal),
    staleTime: Infinity,
    enabled: !!authToken,
  });
}

export function useDocsList() {
  const authToken = useAuthtoken();
  const organizationId = useSelectedOrganizationId();

  const query = useQuery(docsListQueryOptions(authToken, organizationId));

  useEffect(() => {
    if (!(query.error instanceof Error)) return;
    if (query.error.message === TOKEN_INVALID) {
      logout();
    } else {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onDocsGet',
          open: true,
          message: query.error.message,
          severity: 'error',
        });
    }
  }, [query.error]);

  return query;
}
